import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { XCircle, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDisplayName } from '@/lib/formatName';
import type { PolicyVersion } from '@/hooks/usePolicy';

interface RejectionReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: PolicyVersion;
  policyId: string;
  policyName: string;
  onUploadNew?: () => void;
}

export function RejectionReasonDialog({
  open,
  onOpenChange,
  version,
  onUploadNew,
}: RejectionReasonDialogProps) {
  const rejectedByName = formatDisplayName(version.rejected_by_profile?.full_name ?? 'Unknown', version.rejected_by_profile?.credentials);
  const rejectedAt = version.rejected_at
    ? format(new Date(version.rejected_at), 'MMMM d, yyyy \'at\' h:mm a')
    : '—';

  function handleUploadNew() {
    onOpenChange(false);
    onUploadNew?.();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            Version {version.version_number} Rejection Details
          </DialogTitle>
          <DialogDescription>
            This version was rejected and cannot be approved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-3 rounded-md border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Rejected by:</span>
              <span className="font-medium">{rejectedByName}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Rejection date:</span>
              <span className="font-medium">{rejectedAt}</span>
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Reason:</span>
            <ScrollArea className="h-[100px] rounded-md border bg-background p-3">
              <p className="text-sm whitespace-pre-wrap">
                {version.rejection_reason || 'No reason provided.'}
              </p>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onUploadNew && (
            <Button onClick={handleUploadNew} className="gap-1.5">
              <Upload className="h-4 w-4" />
              Upload New Version
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
